/* eslint-disable no-param-reassign, arrow-body-style, no-shadow */
import * as API from 'es6/core/api';
import { LoaderError } from 'es6/core/errors';
import AggregateMemory from 'es6/models/aggregate-memory';
import Goal from 'es6/models/goal';
import Grouping from 'es6/models/grouping';
import { ItemSource } from 'es6/models/session-config';
import StudyNode from 'es6/models/study-node';
import ItemPackage from 'es6/models/item-package';
import Memory from 'es6/models/memory';
import Logger from 'es6/services/logger';
import * as ImageManager from 'es6/services/image-manager';
import SoundManager from 'es6/services/sound-manager';
import Config from 'es6/core/config';

/**
 * These are the loader classes. They are used for making
 * asynchronous calls and can be resolved into promises. They also
 * have a consistent API that should be defined when creating
 * new loaders (easily done by extending the Loader class).
 *
 * abort() - Aborts any pending promise.
 * then(resolve, reject) - Implementation of the then object for the promise.
 */
class Loader {
  abort() {
    this.requests.forEach(request => request.abort());
  }

  get requests() {
    if (!this._requests) {
      this._requests = [];
    }

    return this._requests;
  }

  then(...args) {
    return this._promise.then(...args);
  }

  _setPromise(promise) {
    if (!(promise instanceof Promise)) {
      throw new LoaderError('Promise undefined');
    }

    this._promise = promise.catch((reason) => {
      if (reason.name === 'AjaxAbortedError') {
        Logger.info('Loader Aborted', reason);
      } else if (reason.name === 'AjaxError') {
        Logger.error('Loader Error', reason, reason.stack);
      } else if (reason.name === 'LoaderError') {
        Logger.error('Loader Error', reason, reason.stack);
      } else {
        Logger.error('Unexpected Loading Error', reason, reason.stack);
      }

      throw reason;
    });
  }
}

/*
 * Helper method for the asset loaders.
 */
function getImageAndSoundAssets(assets, entity, transcodeOptions) {
  if (entity.sound) {
    assets.sounds.push(entity.sound);
  }

  if (entity.getImage()) {
    let img = entity.getImage();

    if (transcodeOptions) {
      img = ImageManager.getTranscodedUrl(img, transcodeOptions);
    }
    assets.images.push(img);
  }

  return assets;
}

/**
 * Loads image and sound assets.
 *
 * assets:
 *    images: Array of image URLs.
 *    sounds: Array of sound URLs.
 */
export class Assets extends Loader {
  constructor(assets = {}) {
    super();

    assets = _.defaults(assets, {
      images: [],
      sounds: [],
    });

    /**
     * Flash player breaks when the files are not unique.
     */
    assets.images = _.unique(assets.images);
    assets.sounds = _.unique(assets.sounds);

    const promise = new Promise((resolve, reject) => {
      let imagesLoading = assets.images.length;
      let soundsLoading = assets.sounds.length;

      const hasResolved = () => {
        return imagesLoading === 0 && soundsLoading === 0;
      };

      if (hasResolved()) {
        resolve(true);
      }

      // Loop through images, if the images are done loading
      // resolve the promise. If they fail, reject the promise.
      _.each(assets.images, (img) => {
        ImageManager.load(img).then(() => {
          imagesLoading--;

          if (hasResolved()) {
            resolve(true);
          }
        }).catch((reason) => {
          imagesLoading--;

          if (hasResolved()) {
            resolve(true);
          }

          Logger.error('Error loading image asset', img, reason);
        });
      });

      // Use sound manager to load sounds.
      SoundManager.load(assets.sounds, (sound, success) => {
        if (!success) {
          Logger.error('Error loading sound asset', sound);
        }

        soundsLoading--;
        if (hasResolved()) {
          resolve(true);
        }
      }, () => {});
    });

    this._setPromise(promise);
  }
}

/**
 * Loads none item package and sentence related data.
 */
export class GoalAssets extends Assets {
  constructor(goal = {}) {
    const assets = {
      images: [],
      sounds: [],
    };

    if (goal.icon) {
      assets.images.push(goal.icon);
    }

    super(assets);
  }
}

/**
 * Helper loader for loading item package assets.
 */
export class ItemPackageAssets extends Assets {
  /**
   * @param {ItemPackage[]} itemPackages
   * @param {Boolean} includeSentenceAssets
   */
  constructor(itemPackages, includeSentenceAssets = true) {
    const assets = {
      images: [],
      sounds: [],
    };

    // Iterate through the item packages.
    _.each(itemPackages, (itemPackage) => {
      // Get assets directly on the itemPackage.
      getImageAndSoundAssets(
        assets,
        itemPackage,
        Config.transcodingSettings.appItemImage
      );

      // Get assets on its sentences.
      if (includeSentenceAssets) {
        itemPackage.sentences.forEach((sentence) => {
          getImageAndSoundAssets(
            assets,
            sentence,
            Config.transcodingSettings.appSentenceImage
          );
        });
      }
    });

    super(assets);
  }
}

/**
 * Helper function for creating an Asset Promise for sentence packages.
 *
 * sentences: [Sentence, Sentence, ...]
 */
export class SentenceAssets extends Assets {
  constructor(sentences) {
    const assets = {
      images: [],
      sounds: [],
    };

    sentences.forEach((sentence) => {
      getImageAndSoundAssets(
        assets,
        sentence,
        Config.transcodingSettings.appSentenceImage
      );
    });

    super(assets);
  }
}

/**
 * Used to primarily cache and load data from an API.
 */
class APILoader extends Loader {
  /**
   * Override the default abort behavior. Cached data doesn't
   * have an abort method.
   */
  abort() {
    this.requests.forEach(request => {
      if (request.abort) {
        request.abort();
      }
    });
  }
}

/**
 * GoalsLoader.
 */
let goalResponses = new Map();
export class GoalsLoader extends APILoader {
  /**
   * Constructor for GoalsLoader.
   *
   * @param {Array} goalIds
   */
  constructor(goalIds = []) {
    super();

    this._requests = goalIds.map((id) => {
      return goalResponses.get(id) || API.getGoal(id);
    });

    const promise = Promise.all(this._requests)
      .then(responses => {
        const goals = castToObject(responses, Goal);
        goals.forEach(goal => goalResponses.set(goal.id, goal));
        return goals;
      });

    this._setPromise(promise);
  }

  /**
   * Eliminates the cache for specified goal IDs, goal memories
   * and aggregate memories.
   *
   * @param {Number[]} goalIds
   */
  static invalidate(goalIds = []) {
    goalIds.forEach((id) => goalResponses.delete(id));

    GoalMemoriesLoader.invalidate(goalIds);
    AggregateMemoryLoader.invalidate(goalIds);
  }

  /**
   * Invalidates the whole goal cache.
   */
  static invalidateAll() {
    goalResponses = new Map();

    GoalMemoriesLoader.invalidateAll();
    AggregateMemoryLoader.invalidateAll();
  }
}

/**
 * GoalMemoriesLoader
 */
let goalMemoriesResponses = new Map();
export class GoalMemoriesLoader extends APILoader {
  /**
   * Constructor for GoalMemoriesLoader.
   *
   * @param {Number} goalId
   */
  constructor(goalId) {
    super();

    let promise;

    if (goalMemoriesResponses.has(goalId)) {
      promise = Promise.resolve(goalMemoriesResponses.get(goalId));
    } else {
      const request = API.getGoalMemories(goalId);

      promise = request.then(response => {
        const memories = castToObject(response, Memory);
        goalMemoriesResponses.set(goalId, memories);
        return memories;
      });
    }

    this._setPromise(promise);
  }

  /**
   * Invalidates goal memories for specified goals.
   *
   * @param {Number} goalId
   */
  static invalidate(goalId) {
    goalMemoriesResponses.delete(goalId);
  }

  /**
   * Invalidates all the goal memories.
   */
  static invalidateAll() {
    goalMemoriesResponses = new Map();
  }
}

/**
 * AggregateMemoriesLoader
 */
let aggregateMemoryResponses = new Map();
export class AggregateMemoryLoader extends APILoader {
  /**
   * Constructor for AggregateMemoriesLoader.
   *
   * @param {Number} goalId
   */
  constructor(goalId) {
    super();

    let promise;

    if (aggregateMemoryResponses.has(goalId)) {
      promise = Promise.resolve(aggregateMemoryResponses.get(goalId));
    } else {
      const request = API.getAggregateMemories(goalId);

      promise = request.then(response => {
        const aggregateMemory = new AggregateMemory(response);
        aggregateMemoryResponses.set(goalId, aggregateMemory);
        return aggregateMemory;
      });
    }

    this._setPromise(promise);
  }

  /**
   * Invalidates aggregate memories for specified goals.
   *
   * @param {Number} goalId
   */
  static invalidate(goalId) {
    aggregateMemoryResponses.delete(goalId);
  }

  /**
   * Invalidates all the aggregate memories.
   */
  static invalidateAll() {
    aggregateMemoryResponses = new Map();
  }
}

/**
 * FocusedAggregateMemoriesLoader
 *
 * Only need to maintain a single variable for this. Since it is not
 * mapped across different objects.
 */
let focusedAggregateMemoriesResponse = null;
export class FocusedAggregateMemoriesLoader extends APILoader {
  /**
   * Constructor for FocusedMemoriesLoader.
   */
  constructor() {
    super();

    // If focusedAggregateMemoriesResponse is not null, use that. Otherwise,
    // retrieve the data from the API.
    this._requests = [focusedAggregateMemoriesResponse || API.getFocusedAggregateMemories()];

    const promise = Promise.all(this._requests)
      .then(responses => {
        const response = responses[0];

        response.goals = castToObject(response.goals, AggregateMemory);
        response.groupings = castToObject(response.groupings, Grouping);

        focusedAggregateMemoriesResponse = response;

        return response;
      });

    this._setPromise(promise);
  }

  /**
   * Invalidates the cache for focused memories.
   */
  static invalidate() {
    focusedAggregateMemoriesResponse = null;
  }
}

/**
 * GoalSuggestionLoader
 *
 * Only need to maintain a single variable for this. Since it is not
 * mapped across different objects.
 */
let goalSuggestionResponse = null;
export class GoalSuggestionLoader extends APILoader {
  /**
   * Constructor for GoalSuggestionLoader.
   *
   * @param {String} languageCode
   */
  constructor(languageCode) {
    super();

    // If goalSuggestionResponse is not null, use that. Otherwise,
    // retrieve the data from the API.
    if (goalSuggestionResponse) {
      this._setPromise(Promise.resolve(goalSuggestionResponse));
    } else {
      const request = API.getGoalSuggestion(languageCode);

      this.requests.push(request);

      const promise = request.then(response => {
        response.goal = new Goal(response.goal);
        response.aggregateMemory = new AggregateMemory(response.memoryAggregate);

        goalSuggestionResponse = response;

        return response;
      })

        // If there is a 404, this means that there is no recommendation
        // course because either the user has not studied anything or
        // has studied everything. Either way, this is not an error
        // in the traditional sense.
        .catch(reason => {
          if (reason.name === 'AjaxError' && reason.code === 404) {
            return null;
          } else {
            throw reason;
          }
        });

      this._setPromise(promise);
    }
  }

  /**
   * Invalidates the cache.
   */
  static invalidate() {
    goalSuggestionResponse = null;
  }
}

/**
 * Creates a thenable for the specific content study mode.
 *
 * @param {Object[]} content
 * @param {Number} content[].contentId
 * @param {Number} content[].goalId
 * @param {Object} studyParameters
 */
export class StudySpecificContent extends Loader {
  constructor(content = [], studyParameters = {}) {
    super();

    // Get unique goal IDs.
    const goalIds = content.map(c => c.goal_id || c.goalId)
      .filter((goalId, i, arr) => arr.indexOf(goalId) === i);

    const apiParams = Object.assign({ content }, studyParameters);

    this.requests.push(
      new GoalsLoader(goalIds),
      API.postSpecificContent(apiParams)
    );

    const promise = Promise.all(this.requests).then(responses => {
      const goals = responses[0];
      const studySession = responses[1];

      return convertDataToModels(goals, studySession, [], [], []);
    });

    this._setPromise(promise);
  }
}

/**
 * Creates a thenable for the single study goal of iKnow.
 *
 * @param {Number} goalId
 * @param {Object} studyParameters    parameters sent to the study API
 */
export class StudyGoal extends Loader {
  constructor(goalId, studyParameters = {}) {
    super();

    this.requests.push(
      new GoalsLoader([goalId]),
      new GoalMemoriesLoader(goalId),
      new AggregateMemoryLoader(goalId)
    );

    // Start by retrieving goal and memory data, this will help assess
    // whether or not the user should study strong items.
    const combined = Promise.all(this.requests).then(responses => {
      const goal = responses[0][0];
      const goalMemories = responses[1];
      const aggregateMemory = responses[2];

      /**
       * If the include strong field is set, ignore this step.
       * NOTE: By default, sentence trainer always sends includeStrong.
       * If this changes, there will need to be new logic to handle
       * retrieving the sentence count.
       */
      if (studyParameters && !studyParameters.includeStrong) {
        const newExcluded = studyParameters.sessionItemSource === ItemSource.REVIEW;
        const weakExcluded = studyParameters.sessionItemSource === ItemSource.NEW;
        const newItemsCount = goal.getNewItemsCount(aggregateMemory);
        const weakItemsCount = aggregateMemory.items.eligibleItemsCount;

        if ((weakExcluded || weakItemsCount === 0) && (newExcluded || newItemsCount === 0)) {
          studyParameters.includeStrong = true;
        }
      }

      const studyResponse = API.getStudyData(goalId, studyParameters);
      this.requests.push(studyResponse);

      return Promise.all([
        goal,
        studyResponse,
        goalMemories,
        aggregateMemory,
      ]);
    }).then(data => {
      // Cast data to the appropriate types and store the responses.
      return convertDataToModels(
        [data[0]],
        data[1],
        data[2],
        [data[3]],
        []
      );
    });

    // Set this as the thenable.
    this._setPromise(combined);
  }
}

/**
 * Creates a thenable for the single study goal of iKnow.
 *
 * @param {Number} goalId
 * @param {Object} studyParameters    parameters sent to the study API
 */
export class StudyAll extends Loader {
  constructor(language, studyParameters = {}) {
    super();

    // Create an initial study request based on default parameters.
    const initialStudyRequest = API.getStudyAllData(language, studyParameters);
    this.requests.push(initialStudyRequest);

    // Check to see if there are any weak study items. If not,
    // add the includeStrong flag and redo the request. This will
    // make sure the user always has something to study.
    const studyDataRequest = initialStudyRequest.then(studyData => {
      // Don't do anything if strong is already included.
      if (studyParameters && !studyParameters.includeStrong) {
        const readyStudyNodes = studyData.filter(studyNode => {
          return (new StudyNode(studyNode)).isReady();
        });

        // There are no ready items, include strong and return the next request.
        if (readyStudyNodes.length === 0) {
          studyParameters.includeStrong = true;

          const studyStrongRequest = API.getStudyAllData(language, studyParameters);
          this.requests.push(studyStrongRequest);

          return studyStrongRequest;
        }
      }

      // Default to the initial response.
      return studyData;
    });

    // Because getting goals is dependent on the study data,
    // chain it in a separate promise.
    const goals = studyDataRequest.then(studyData => {
      const goalIds = _.chain(studyData)
        .pluck('goalId')
        .compact()
        .unique()
        .value();

      const goalLoader = new GoalsLoader(goalIds);
      this.requests.push(goalLoader);

      return goalLoader;
    });

    // Add focused memory loader as a request that can be cancelled.
    const focusedMemoriesLoader = new FocusedAggregateMemoriesLoader();
    this.requests.push(focusedMemoriesLoader);

    const combined = Promise.all([
      goals,
      studyDataRequest,
      focusedMemoriesLoader,
    ]).then(responses => {
      const focusedMemories = responses[2];
      const loadedModels = convertDataToModels(
        responses[0],
        responses[1],
        [],
        focusedMemories.goals,
        focusedMemories.groupings
      );

      // Get the language grouping for this goal.
      loadedModels.grouping = loadedModels.groupings.find(grouping => {
        return language === grouping.grouping.cueLanguageCode;
      });

      // Just pull the relevant aggregate memories.
      loadedModels.aggregateMemories = loadedModels.aggregateMemories.filter(memory => {
        return loadedModels.grouping.hasGoal(memory.goalId);
      });

      return loadedModels;
    });

    this._setPromise(combined);
  }
}

const convertDataToModels = (goals, studySession, memories, aggregateMemories, groupings) => {
  return {
    goals:             castToObject(goals, Goal),
    studySession:      castToObject(studySession, StudyNode),
    memories:          castToObject(memories, Memory),
    aggregateMemories: castToObject(aggregateMemories, AggregateMemory),
    groupings:         castToObject(groupings, Grouping),
    grouping:          null,
  };
};

/*
 * Helper function to cast array of object literals to a particular object
 * instance. This method will not update/override any objects that have
 * already been instantiated.
 */
const castToObject = (array, castTo) => {
  return array.map((x) => {
    return x instanceof castTo ? x : new castTo(x);
  });
};

export class Diagnostic extends Loader {
  constructor(diagId, params = {}) {
    super();

    const diagData = API.getDiagnosticData(diagId, params);
    const diagStudy = API.getDiagnosticStudyData(diagId, _.defaults(params, { items_per_band: 10 }))
      .then(diagStudy => {
        // Cast the study Items to StudyNodes.
        _.each(diagStudy, studyBand => {
          studyBand.content = castToObject(studyBand.content, StudyNode);
        });
        return diagStudy;
      });
    this.requests.push(diagData, diagStudy);

    const goals = diagData.then((diagData) => {
      const goals = _.chain(diagData.bands)
        .pluck('diagnosticGoalId')
        .compact()
        .map((goalId) => {
          return API.getGoal(goalId, params);
        })
        .each((request) => {
          this.requests.push(request);
        })
        .value();

      return Promise.all(goals);
    }).then(goals => {
      // Cast the goalItems to ItemPackages.
      _.each(goals, goal => {
        goal.goalItems = castToObject(goal.goalItems, ItemPackage);
      });
      return goals;
    });

    const combined = Promise.all([
      diagData,
      diagStudy,
      goals,
    ]).then(data => {
      return {
        diagData:  data[0],
        diagStudy: data[1],
        goals:     data[2],
      };
    });

    this._setPromise(combined);
  }
}
